<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAxios } from 'dashboard/composables/useAxios';
import { useAlert } from 'dashboard/composables';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import VoiceAgentForm from './VoiceAgentForm.vue';

const props = defineProps({
  selectedVoiceAgent: {
    type: Object,
    default: () => ({}),
  },
  type: {
    type: String,
    default: 'create',
    validator: value => ['create', 'edit'].includes(value),
  },
});

const emit = defineEmits(['close', 'created']);
const { t } = useI18n();
const axios = useAxios();

const dialogRef = ref(null);
const voiceAgentForm = ref(null);

const i18nKey = computed(
  () => `AI_VOICE.VOICE_AGENTS.${props.type.toUpperCase()}`
);

const createVoiceAgent = async voiceAgentDetails => {
  const { data } = await axios.post(
    '/api/v1/accounts/:account_id/voice_agents',
    {
      voice_agent: voiceAgentDetails,
    }
  );
  return data;
};

const updateVoiceAgent = async voiceAgentDetails => {
  const { data } = await axios.put(
    `/api/v1/accounts/:account_id/voice_agents/${props.selectedVoiceAgent.id}`,
    {
      voice_agent: voiceAgentDetails,
    }
  );
  return data;
};

const handleSubmit = async updatedVoiceAgent => {
  try {
    let voiceAgent;
    if (props.type === 'edit') {
      voiceAgent = await updateVoiceAgent(updatedVoiceAgent);
    } else {
      voiceAgent = await createVoiceAgent(updatedVoiceAgent);
    }
    useAlert(t(`${i18nKey.value}.SUCCESS_MESSAGE`));
    emit('created', voiceAgent);
    dialogRef.value.close();
  } catch (error) {
    const errorMessage = error?.message || t(`${i18nKey.value}.ERROR_MESSAGE`);
    useAlert(errorMessage);
  }
};

const handleClose = () => {
  emit('close');
};

const handleCancel = () => {
  dialogRef.value.close();
};

defineExpose({ dialogRef });
</script>

<template>
  <Dialog
    ref="dialogRef"
    type="edit"
    :title="t(`${i18nKey}.TITLE`)"
    :description="t(`${i18nKey}.DESCRIPTION`)"
    :show-cancel-button="false"
    :show-confirm-button="false"
    overflow-y-auto
    @close="handleClose"
  >
    <VoiceAgentForm
      ref="voiceAgentForm"
      :mode="type"
      :voice-agent="selectedVoiceAgent"
      @submit="handleSubmit"
      @cancel="handleCancel"
    />
    <template #footer />
  </Dialog>
</template>
